import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Reveal } from "./Reveal";
import { EventCard } from "./EventCard";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";

export function Events() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const isRtl = lang === "he";
  const [emblaApi, setEmblaApi] = useState(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const { data: events = [] } = useQuery({
    queryKey: ["events", "upcoming"],
    queryFn: async () => (await api.get("/events?upcoming=true")).data,
  });

  const items = events.slice(0, 5);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
      setSelected(emblaApi.selectedScrollSnap());
    };
    setSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => {
      setSnaps(emblaApi.scrollSnapList());
      onSelect();
    });
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, items.length]);

  return (
    <section id="events" data-testid="events-section" className="relative z-10 pt-24 sm:pt-32 pb-12 sm:pb-14 bg-[#E8E6E1]/50 border-y border-[#0F172A]/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="events.eyebrow" as="span" /></span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight leading-tight"><EditableText k="events.title" as="span" /></h2>
            <p className="mt-3 max-w-md text-[#475569]"><EditableText k="events.desc" as="span" multiline /></p>
          </Reveal>
          <Reveal delay={0.1}>
            <button
              data-testid="view-all-events"
              onClick={() => navigate("/events")}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] hover:gap-3 transition-[gap]"
            >
              {t("events.viewall")} <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>

        {items.length === 0 ? (
          <p data-testid="events-empty" className="text-[#475569]">{t("events.empty")}</p>
        ) : (
          <Reveal>
            <Carousel key={lang} setApi={setEmblaApi} opts={{ align: "start", loop: false, direction: isRtl ? "rtl" : "ltr" }} data-testid="events-carousel">
              <CarouselContent className="-ml-6">
                {items.map((ev, i) => (
                  <CarouselItem key={ev.id} className="pl-6 basis-full sm:basis-1/2 lg:basis-1/3">
                    <EventCard event={ev} index={i} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>

            {items.length > 1 && (
              <div className="mt-10 hidden md:flex items-center justify-center gap-4" data-testid="events-carousel-nav">
                <button
                  data-testid="events-carousel-prev"
                  aria-label="Previous events"
                  onClick={() => emblaApi?.scrollPrev()}
                  disabled={!canPrev}
                  className="grid h-[52px] w-[52px] place-items-center rounded-full bg-[#F4EEE2] text-[#0F172A] shadow-[0_6px_18px_rgba(15,23,42,0.08)] transition-all hover:bg-white hover:text-[#BE923C] hover:shadow-[0_10px_24px_rgba(15,23,42,0.12)] disabled:opacity-30 disabled:hover:bg-[#F4EEE2] disabled:hover:text-[#0F172A] disabled:hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)]"
                >
                  <ArrowLeft size={20} />
                </button>
                <button
                  data-testid="events-carousel-next"
                  aria-label="Next events"
                  onClick={() => emblaApi?.scrollNext()}
                  disabled={!canNext}
                  className="grid h-[52px] w-[52px] place-items-center rounded-full bg-[#F4EEE2] text-[#0F172A] shadow-[0_6px_18px_rgba(15,23,42,0.08)] transition-all hover:bg-white hover:text-[#BE923C] hover:shadow-[0_10px_24px_rgba(15,23,42,0.12)] disabled:opacity-30 disabled:hover:bg-[#F4EEE2] disabled:hover:text-[#0F172A] disabled:hover:shadow-[0_6px_18px_rgba(15,23,42,0.08)]"
                >
                  <ArrowRight size={20} />
                </button>
              </div>
            )}

            {snaps.length > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2 md:hidden" data-testid="events-carousel-dots">
                {snaps.map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => emblaApi?.scrollTo(i)}
                    className={`h-2 rounded-full transition-all ${selected === i ? "w-6 bg-[#BE923C]" : "w-2 bg-[#0F172A]/20"}`}
                  />
                ))}
              </div>
            )}
          </Reveal>
        )}
      </div>
    </section>
  );
}
